// Conocimiento view — typed CRUD on per-scope /api/.../knowledge entries plus
// `ask`. Scope can be a person or a group; the selector lists both. Reads use
// AbortSignal; in-flight loads are dropped on scope change and on unmount.
// Editor uses a custom modal (knowledge-editor) instead of formModal because
// the text field exceeds formModal's 2000 char limit and file import needs
// preview + explicit destination confirmation.
import { h, toast, clear, confirmModal } from '../components/dom.js';
import { renderHeader } from '../components/header.js';
import type { ApiClient } from '../../api/client.js';
import type { KnowledgeScope as ApiScope } from '../../api/people-types.js';
import { openKnowledgeEditor } from '../components/knowledge-editor.js';

type ScopeKind = 'person' | 'group';

type Scope = { kind: ScopeKind; id: string; label: string };

type Entry = {
  id: string;
  title: string;
  text: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
};

type KnowledgeHost = {
  api(): ApiClient;
  userId: string;
  initialScope?: Scope;
};

export type { Scope as ConocimientoScope };

const PERSONAL_LIMIT = 100;
const GROUP_LIMIT = 200;
const ASK_MAX = 2000;

function truncate(s: string, n: number): string {
  return s.length <= n ? s : `${s.slice(0, n)}…`;
}

function limitForScope(scope: Scope): number {
  return scope.kind === 'group' ? GROUP_LIMIT : PERSONAL_LIMIT;
}

export function renderConocimiento(host: KnowledgeHost): {
  el: HTMLElement;
  update?(ctx: { param?: string | null }): void;
  destroy?(): void;
} {
  const section = document.createElement('section');
  section.className = 'view view-conocimiento conocimiento-view';
  section.setAttribute('aria-labelledby', 'conocimiento-title');

  const header = renderHeader('conocimiento', { subtitle: 'La información que compartes con ASIS.' });
  section.append(header);

  const scopesRow = h('div', { class: 'conocimiento-scopes knowledge-scope-row' });
  const personSelect = h('select', { class: 'input', 'aria-label': 'Persona' }) as HTMLSelectElement;
  const groupSelect = h('select', { class: 'input', 'aria-label': 'Grupo' }) as HTMLSelectElement;
  const kindLabel = h('p', { class: 'conocimiento-kind-hint muted-text' });
  scopesRow.append(
    h('label', { class: 'field conocimiento-field' }, [h('span', {}, ['Persona']), personSelect]),
    h('label', { class: 'field conocimiento-field' }, [h('span', {}, ['Grupo']), groupSelect]),
    kindLabel,
  );
  section.append(scopesRow);

  const toolbar = h('div', { class: 'list-toolbar' });
  const btnCreate = h('button', { class: 'btn primary', type: 'button' }, ['Nueva entrada']);
  toolbar.append(
    h('h3', { class: 'list-toolbar-title', id: 'conocimiento-title' }, ['Entradas']),
    h('div', { class: 'list-toolbar-actions' }, [btnCreate]),
  );
  section.append(toolbar);

  const listPanel = h('section', { class: 'panel list-panel', 'aria-label': 'Entradas de conocimiento' });
  const status = h('p', { class: 'panel-status muted-text', role: 'status' });
  const list = h('ul', { class: 'list tokens conocimiento-list', role: 'list' });
  listPanel.append(status, list);
  section.append(listPanel);
  const scopeInvitation = h('div', { class: 'knowledge-invitation empty' }, [h('h3', {}, ['Cada persona y grupo tiene su espacio']), h('p', {}, ['Elige una persona o un grupo para ver su información. También puedes empezar una conversación con ASIS.']), h('a', { class: 'btn ghost', href: '#/conversar' }, ['Hablar con ASIS'])]);
  section.append(scopeInvitation);

  const askPanel = h('section', { class: 'panel ask-panel', 'aria-label': 'Preguntar sobre este espacio' });
  const askRow = h('div', { class: 'ask-row' });
  const askInput = h('textarea', {
    class: 'textarea ask-input',
    rows: 3,
    maxlength: ASK_MAX,
    placeholder: 'Escribe una pregunta sobre este espacio (máx. 2000 caracteres)…',
    'aria-label': 'Pregunta',
  }) as HTMLTextAreaElement;
  const askScopeLabel = h('span', { class: 'ask-scope-label muted-text' });
  const askBtn = h('button', { class: 'btn primary', type: 'button' }, ['Preguntar']);
  askRow.append(askInput, askBtn, askScopeLabel);
  const askStatus = h('p', { class: 'panel-status muted-text', role: 'status' });
  const askAnswer = h('div', { class: 'ask-answer', 'aria-live': 'polite' });
  const askSources = h('ul', { class: 'ask-sources tokens', role: 'list' });
  askPanel.append(askRow, askStatus, askAnswer, askSources);
  const askDetails = h('details', { class: 'secondary-details knowledge-ask-details' }, [h('summary', {}, ['Preguntar sobre este espacio']), askPanel]);
  section.append(askDetails);
  askDetails.addEventListener('keydown', event => { if (event.key === 'Escape') { askDetails.open = false; askDetails.querySelector('summary')?.focus(); } });

  let alive = true;
  let gen = 0;
  let loadAbort: AbortController | null = null;
  let askAbort: AbortController | null = null;
  let writeInFlight = false;
  const lifetime = new AbortController();
  let editorAbort: AbortController | null = null;
  let writeAbort: AbortController | null = null;

  const people: Array<{ id: string; displayName: string; kind: 'self' | 'guest'; userId: string | null }> = [];
  const groups: Array<{ id: string; name: string; role: 'owner' | 'member' }> = [];
  let selfPersonId: string | null = null;
  let current: Scope | null = null;
  const entries = new Map<string, Entry>();

  const guard = (): boolean => alive && section.isConnected;

  function buildPersonLabels(): Array<{ id: string; label: string }> {
    const out: Array<{ id: string; label: string }> = [];
    if (selfPersonId) {
      const self = people.find(p => p.id === selfPersonId);
      out.push({ id: selfPersonId, label: self ? `Yo · ${self.displayName}` : 'Yo' });
    }
    for (const p of people) {
      if (p.id === selfPersonId) continue;
      out.push({ id: p.id, label: p.displayName });
    }
    return out;
  }

  function buildGroupLabels(): Array<{ id: string; label: string }> {
    return groups.map(g => ({ id: g.id, label: g.name }));
  }

  function fillSelectors() {
    const personItems = buildPersonLabels();
    personSelect.replaceChildren();
    personSelect.append(h('option', { value: '' }, ['— Elegir persona —']));
    for (const item of personItems) {
      personSelect.append(h('option', { value: `${item.id}` }, [item.label]));
    }
    const groupItems = buildGroupLabels();
    groupSelect.replaceChildren();
    groupSelect.append(h('option', { value: '' }, ['— Elegir grupo —']));
    for (const item of groupItems) {
      groupSelect.append(h('option', { value: `${item.id}` }, [item.label]));
    }
  }

  function findScopeFromUI(): Scope | null {
    const pid = personSelect.value;
    if (pid) {
      const item = buildPersonLabels().find(i => i.id === pid);
      if (item) return { kind: 'person', id: item.id, label: item.label };
    }
    const gid = groupSelect.value;
    if (gid) {
      const item = buildGroupLabels().find(i => i.id === gid);
      if (item) return { kind: 'group', id: item.id, label: item.label };
    }
    return null;
  }

  function syncSelectorPair(next: Scope | null) {
    scopeInvitation.hidden = next !== null;
    personSelect.value = next && next.kind === 'person' ? next.id : '';
    groupSelect.value = next && next.kind === 'group' ? next.id : '';
    askScopeLabel.textContent = next ? `Espacio: ${next.label}` : '';
    askBtn.disabled = !next;
    btnCreate.disabled = !next;
    if (!next) kindLabel.textContent = 'Elige un espacio para ver sus entradas.';
    else kindLabel.textContent = next.kind === 'group'
      ? `Grupo: las entradas son compartidas. Capacidad ${GROUP_LIMIT}.`
      : `Persona: las entradas solo se ven en este espacio. Capacidad ${PERSONAL_LIMIT}.`;
  }

  function setAnswer(kind: 'idle' | 'loading' | 'ok' | 'err', payload?: { text?: string; sources?: Array<{ id: string; title: string }> }) {
    askStatus.textContent = '';
    askAnswer.replaceChildren();
    askSources.replaceChildren();
    if (kind === 'idle') return;
    if (kind === 'loading') {
      askStatus.textContent = 'Pensando…';
      return;
    }
    if (kind === 'err') {
      askStatus.textContent = payload?.text ?? 'No se pudo responder. Inténtalo de nuevo.';
      const retry = h('button', { class: 'btn ghost', type: 'button' }, ['Reintentar']);
      retry.addEventListener('click', () => { void runAsk(); });
      askStatus.append(' ', retry);
      return;
    }
    const safe = (payload?.text ?? '').trim();
    if (!safe) {
      askStatus.textContent = 'No hay respuesta para esta pregunta.';
      return;
    }
    const body = h('p', { class: 'ask-answer-body' });
    body.textContent = safe;
    askAnswer.append(body);
    const srcs = payload?.sources ?? [];
    if (srcs.length) {
      askStatus.textContent = `Fuentes citadas (${srcs.length}):`;
      for (const src of srcs) {
        askSources.append(h('li', { class: 'token ask-source' }, [src.title]));
      }
    }
  }

  function renderEntries(scoped: Scope) {
    list.replaceChildren();
    status.textContent = `${entries.size} entradas en «${scoped.label}».`;
    btnCreate.disabled = entries.size >= limitForScope(scoped);
    if (entries.size === 0) {
      status.textContent = '';
      list.append(h('li', { class: 'empty' }, [h('h3', {}, ['Todo empieza con algo que compartir']), h('p', {}, [`Sin entradas todavía en «${scoped.label}». Crea la primera con «Nueva entrada».`]), h('a', { class: 'btn ghost', href: '#/conversar' }, ['Hablar con ASIS'])]));
      return;
    }
    const sorted = [...entries.values()].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    for (const e of sorted) {
      const li = h('li', { class: 'token conocimiento-entry' });
      const title = h('h4', { class: 'entry-title' }, [truncate(e.title, 120)]);
      const preview = h('p', { class: 'entry-preview muted-text' }, [truncate(e.text, 240)]);
      const meta = h('p', { class: 'entry-meta muted-text' }, [`Actualizado ${new Date(e.updatedAt).toLocaleString()}`]);
      const actions = h('div', { class: 'entry-actions' });
      const btnEdit = h('button', { class: 'btn ghost', type: 'button' }, ['Editar']);
      const btnDel = h('button', { class: 'btn danger', type: 'button' }, ['Borrar']);
      if (canEditEntry(scoped,e)) actions.append(btnEdit, btnDel);
      li.append(title, preview, meta, actions);
      li.dataset.entryId = e.id;
      btnEdit.addEventListener('click', () => { void editEntry(scoped, e); });
      btnDel.addEventListener('click', () => { void deleteEntry(scoped, e); });
      list.append(li);
    }
  }

  function canEditEntry(scope: Scope, e: Entry): boolean {
    if (scope.kind === 'person') return true;
    const g = groups.find(g => g.id === scope.id);
    if (!g) return false;
    if (g.role === 'owner') return true;
    return e.createdBy === host.userId;
  }

  async function loadCurrent(scoped: Scope) {
    const myGen = ++gen;
    const ctrl = new AbortController();
    loadAbort?.abort();
    loadAbort = ctrl;
    status.textContent = 'Cargando entradas…';
    list.replaceChildren();
    entries.clear();
    try {
      const cap = limitForScope(scoped);
      const list0 = await host.api().listKnowledge(scoped, ctrl.signal);
      if (!guard() || myGen !== gen || ctrl.signal.aborted) return;
      const items = list0.entries.slice(0, cap);
      for (const e of items) entries.set(e.id, e);
      renderEntries(scoped);
    } catch (err) {
      if (!guard() || myGen !== gen || ctrl.signal.aborted) return;
      if ((err as Error).name === 'AbortError') return;
      status.textContent = `Error al cargar entradas: ${(err as Error).message}`;
    } finally {
      if (loadAbort === ctrl) loadAbort = null;
    }
  }

  async function saveEntry(scoped: Scope, entry?: Entry) {
    if (!guard() || writeInFlight || current !== scoped) return;
    if (entry && !canEditEntry(scoped, entry)) return;
    if (!entry && entries.size >= limitForScope(scoped)) { toast('Este espacio ha alcanzado su límite de entradas.', 'error'); return; }
    const capturedGeneration = gen;
    const editor = new AbortController();
    editorAbort?.abort(); editorAbort = editor;
    writeInFlight = true;
    try {
      await openKnowledgeEditor({
        title: entry ? 'Editar entrada' : 'Nueva entrada', scope: scoped,
        ...(entry ? {initialTitle:entry.title,initialText:entry.text} : {}),
        signal: editor.signal,
        onSave: async values => {
          if (!guard() || editor.signal.aborted || current !== scoped || gen !== capturedGeneration) throw new Error('El espacio ha cambiado. Abre de nuevo la entrada.');
          const ctrl = new AbortController(); writeAbort = ctrl;
          try {
            if (entry) await host.api().patchKnowledge(scoped,entry.id,values,ctrl.signal);
            else await host.api().createKnowledge(scoped,values,ctrl.signal);
            if (!guard() || ctrl.signal.aborted || editor.signal.aborted || current !== scoped || gen !== capturedGeneration) return;
            toast(entry ? 'Entrada actualizada.' : 'Entrada creada.', 'ok');
            await loadCurrent(scoped);
          } finally { if (writeAbort === ctrl) writeAbort = null; }
        },
      });
    } finally { if (editorAbort === editor) { editorAbort = null; writeInFlight = false; } }
  }
  const createEntry = (scope: Scope) => saveEntry(scope);
  const editEntry = (scope: Scope, entry: Entry) => saveEntry(scope, entry);

  async function deleteEntry(scoped: Scope, entry: Entry) {
    if (!guard() || writeInFlight || current !== scoped || !canEditEntry(scoped,entry)) return;
    const capturedGeneration = gen;
    const ok = await confirmModal({title:'Borrar entrada',body:`¿Borrar «${entry.title}» de «${scoped.label}»?`,confirmLabel:'Borrar',danger:true});
    if (!ok || !guard() || writeInFlight || current !== scoped || gen !== capturedGeneration) return;
    writeInFlight = true;
    const ctrl = new AbortController(); writeAbort = ctrl;
    try {
      await host.api().deleteKnowledge(scoped,entry.id,ctrl.signal);
      if (!guard() || ctrl.signal.aborted || current !== scoped || gen !== capturedGeneration) return;
      toast('Entrada borrada.', 'ok'); await loadCurrent(scoped);
    } catch (err) { if (guard() && !ctrl.signal.aborted && current === scoped) toast((err as Error).message, 'error'); }
    finally { if (writeAbort === ctrl) { writeAbort = null; writeInFlight = false; } }
  }

  async function runAsk() {
    if (!guard() || !current || askAbort) return;
    const text = askInput.value.trim();
    if (!text) {
      askStatus.textContent = 'Escribe una pregunta antes de pulsar Preguntar.';
      return;
    }
    if (text.length > ASK_MAX) {
      askStatus.textContent = `Máximo ${ASK_MAX} caracteres.`;
      return;
    }
    const captured = current;
    const ctrl = new AbortController();
    askAbort = ctrl;
    setAnswer('loading');
    askBtn.disabled = true;
    try {
      const res = await host.api().askKnowledge({ scope: { kind: captured.kind, id: captured.id }, text }, ctrl.signal, 30000);
      if (!guard() || ctrl.signal.aborted || captured !== current) return;
      setAnswer('ok', { text: res.text, sources: res.sources ?? [] });
      if (captured.kind === 'group') {
        askStatus.textContent = 'Las respuestas citan entradas del grupo seleccionado, nunca personales.';
      }
    } catch (err) {
      if (!guard() || ctrl.signal.aborted || captured !== current) return;
      if ((err as Error).name === 'AbortError') return;
      setAnswer('err', { text: (err as Error).message });
    } finally {
      if (askAbort === ctrl) askAbort = null;
      if (guard() && current === captured) askBtn.disabled = !current;
    }
  }

  function applyScope(next: Scope | null) {
    if (!guard()) return;
    gen++;
    loadAbort?.abort(); askAbort?.abort(); editorAbort?.abort(); writeAbort?.abort();
    loadAbort = askAbort = editorAbort = writeAbort = null;
    writeInFlight = false;
    current = next;
    syncSelectorPair(next);
    entries.clear(); list.replaceChildren(); status.textContent = '';
    askInput.value = ''; setAnswer('idle');
    if (next) void loadCurrent(next);
  }

  personSelect.addEventListener('change', () => {
    if (personSelect.value) groupSelect.value = '';
    applyScope(findScopeFromUI());
  });
  groupSelect.addEventListener('change', () => {
    if (groupSelect.value) personSelect.value = '';
    applyScope(findScopeFromUI());
  });
  btnCreate.addEventListener('click', () => { if (current) void createEntry(current); });
  askBtn.addEventListener('click', () => { void runAsk(); });
  askInput.addEventListener('keydown', (ev: KeyboardEvent) => {
    if ((ev.metaKey || ev.ctrlKey) && ev.key === 'Enter') {
      ev.preventDefault();
      void runAsk();
    }
  });

  (async function init() {
    fillSelectors();
    syncSelectorPair(null);
    try {
      const [p, g] = await Promise.all([
        host.api().listPeople(lifetime.signal),
        host.api().listGroups(lifetime.signal),
      ]);
      if (!guard()) return;
      people.splice(0, people.length, ...p.people);
      groups.splice(0, groups.length, ...g.groups);
      const self0 = people.find(x => x.kind === 'self');
      selfPersonId = self0?.id ?? null;
      fillSelectors();
      let initial: Scope | null = null;
      const seed = host.initialScope;
      if (seed) {
        if (seed.kind === 'person' && people.some(x => x.id === seed.id)) initial = seed;
        else if (seed.kind === 'group' && groups.some(x => x.id === seed.id)) initial = seed;
      }
      syncSelectorPair(initial);
      if (initial) applyScope(initial);
    } catch (err) {
      if (!guard()) return;
      scopeInvitation.hidden = true;
      status.textContent = `Error al listar espacios: ${(err as Error).message}`;
    }
  })();

  return {
    el: section,
    destroy() {
      alive = false;
      gen++;
      loadAbort?.abort();
      askAbort?.abort(); editorAbort?.abort(); writeAbort?.abort(); lifetime.abort();
      clear(section);
    },
  };
}

export type { ApiScope };
